/**
 * OMS BFF contracts (OMS-CLOUD-006).
 *
 * Two Lambdas already define the read side this contract has to fit:
 * - GetOrderStatuses returns `{ key, value }[]`. `statuses` on GET /oms/reference is that list, unchanged.
 * - getOrders caches id, books, status, quantities, OLE dates, side and instrument fields per datasource and date window.
 *   GET /oms/orders maps that row onto `OmsOrder` (integer id, calendar `tifDate`, positive quantities, and the
 *   instrument fields when the cache has them). The BFF owns the window: omitted from/to means today and tomorrow.
 *
 * Datasource is the JWT. These schemas never carry a datasource, an OLE date id, or a session ticket.
 */
import { Type, type Static } from '@sinclair/typebox';
import { IsoDate, IsoDateTime } from './common';
import { StringEnum, createSchema } from '../../util/schema';
import { OMS_ORDER_STATUS_LABELS } from '../omsLabels';

export const OmsSide = StringEnum(['BUY', 'SELL']);
export type OmsSide = Static<typeof OmsSide>;

export const OmsCheck = StringEnum(['PASS', 'WARN', 'BLOCK', 'NOT_CHECKED']);
export type OmsCheck = Static<typeof OmsCheck>;

export const OmsPositionView = StringEnum(['total', 'tier', 'account']);
export type OmsPositionView = Static<typeof OmsPositionView>;

export const OmsFillAllocation = StringEnum(['all', 'allocated', 'unallocated']);
export type OmsFillAllocation = Static<typeof OmsFillAllocation>;

export const OmsFillPageSize = StringEnum(['50', '100', '150', '200']);
export type OmsFillPageSize = Static<typeof OmsFillPageSize>;

export const OmsFillGroup = StringEnum(['order', 'product']);
export type OmsFillGroup = Static<typeof OmsFillGroup>;

export const OmsRuleStage = StringEnum(['pre', 'post']);
export type OmsRuleStage = Static<typeof OmsRuleStage>;

/** What a hit does. Block stops the ticket. Warn needs an acknowledgement. */
export const OmsRuleEffect = StringEnum(['block', 'warn', 'block-or-warn'], {
  description: 'What a hit does when HedgeFacts Applications runs the check. Block stops the ticket. Warn needs an acknowledgement.',
});
export type OmsRuleEffect = Static<typeof OmsRuleEffect>;

/** Where the check runs. Coming soon is in the catalogue and is not evaluated. */
export const OmsRuleSource = StringEnum(['desktop', 'cloud', 'coming-soon']);
export type OmsRuleSource = Static<typeof OmsRuleSource>;

/** How two values compare. Strings compare trimmed and case-insensitively. A type mismatch or a missing value is unknown. present and missing are never unknown. */
export const OmsRuleOp = StringEnum(['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'notIn', 'sharesAny', 'present', 'missing']);
export type OmsRuleOp = Static<typeof OmsRuleOp>;

/** A value name the checker resolves, such as order.qty or cap.fatFingerMaxQty, or a literal value. */
export const OmsRuleOperand = Type.Union([
  Type.String({ minLength: 1 }),
  Type.Object({ literal: Type.Union([Type.String(), Type.Number(), Type.Boolean(), Type.Array(Type.String())]) }),
]);
export type OmsRuleOperand = Static<typeof OmsRuleOperand>;

export const OmsRuleCondition = Type.Object({
  left: Type.String({ minLength: 1 }),
  op: OmsRuleOp,
  right: Type.Optional(OmsRuleOperand),
});
export type OmsRuleCondition = Static<typeof OmsRuleCondition>;

/** Every condition must hold, in order. The first one that does not hold ends the tier. */
export const OmsRuleTier = Type.Object({
  result: StringEnum(['BLOCK', 'WARN']),
  when: Type.Array(OmsRuleCondition, { minItems: 1 }),
});
export type OmsRuleTier = Static<typeof OmsRuleTier>;

/** A setting the rule reads as cap.<name>. whenUnset is the value used when no stored row matches. */
export const OmsRuleCapDecl = Type.Object({
  name: Type.String({ minLength: 1 }),
  kind: StringEnum(['number', 'boolean', 'date', 'list']),
  whenUnset: Type.Optional(Type.Union([Type.Boolean(), Type.Array(Type.String())])),
});
export type OmsRuleCapDecl = Static<typeof OmsRuleCapDecl>;

/**
 * subjects are the orders the rule judges. against, when set, makes each tier a pair test with the other orders (other.*).
 * appliesWhen must hold for a subject to be judged. The first tier that holds decides the result. No tier is a pass.
 */
export const OmsRuleLogic = Type.Object(
  {
    subjects: StringEnum(['preview', 'working', 'traded']),
    against: Type.Optional(StringEnum(['working', 'traded'])),
    appliesWhen: Type.Array(OmsRuleCondition),
    tiers: Type.Array(OmsRuleTier),
    caps: Type.Array(OmsRuleCapDecl),
  },
  { $id: 'OmsRuleLogic' },
);
export type OmsRuleLogic = Static<typeof OmsRuleLogic>;
export const omsRuleLogicSchema = createSchema(OmsRuleLogic);

export const OmsTransitionOutcome = StringEnum(['DONE', 'REFUSED']);
export type OmsTransitionOutcome = Static<typeof OmsTransitionOutcome>;

export const OmsApprovalStatus = StringEnum(['PENDING', 'APPROVED', 'REJECTED']);
export type OmsApprovalStatus = Static<typeof OmsApprovalStatus>;

export const OmsLayoutTab = StringEnum(['create', 'pretrade', 'monitor', 'post-trade', 'blotter', 'custom']);
export type OmsLayoutTab = Static<typeof OmsLayoutTab>;

const nullableNumber = Type.Union([Type.Number(), Type.Null()]);
const nullableDate = Type.Union([IsoDate, Type.Null()]);

/** One GetOrderStatuses pair. `key` is the on-prem name, `value` is the on-prem label. */
export const OmsDictionaryEntry = Type.Object({
  key: Type.String(),
  value: Type.String(),
});
export type OmsDictionaryEntry = Static<typeof OmsDictionaryEntry>;

export const OmsCheckHit = Type.Object({
  checkName: Type.String(),
  source: Type.String({ description: 'Regulatory, Exchange, or Custom.' }),
  result: OmsCheck,
  message: Type.Optional(Type.String()),
});
export type OmsCheckHit = Static<typeof OmsCheckHit>;

export const OmsAlgoParams = Type.Object({
  childOrderSize: Type.Optional(Type.Number()),
  distance: Type.Optional(Type.Number()),
  intervalSeconds: Type.Optional(Type.Number()),
  maxDuration: Type.Optional(Type.Number()),
});
export type OmsAlgoParams = Static<typeof OmsAlgoParams>;

export const OmsAllocation = Type.Object({
  clearingAccount: Type.Union([Type.String(), Type.Null()], { description: 'Null shows as Unallocated.' }),
  instructionQty: Type.Number(),
  allocPct: Type.Number(),
  allocatedQty: Type.Optional(Type.Number()),
  averageFillPrice: Type.Optional(Type.Number()),
});
export type OmsAllocation = Static<typeof OmsAllocation>;

export const OmsFill = Type.Object({
  execId: Type.String(),
  orderId: Type.Integer({ minimum: 1 }),
  tradeTime: IsoDateTime,
  side: Type.Optional(OmsSide),
  qty: Type.Number(),
  price: Type.Number(),
  exchange: Type.Optional(Type.String()),
  product: Type.Optional(Type.String()),
  bbFullSymbol: Type.String(),
  currency: Type.Optional(Type.String()),
  marketValue: Type.Optional(Type.Number()),
  strategy: Type.Optional(Type.String()),
  clearingAccount: Type.Union([Type.String(), Type.Null()]),
  importerStatus: Type.Optional(Type.String()),
});
export type OmsFill = Static<typeof OmsFill>;

/**
 * Blotter order. Required fields are the order-read cache:
 * id, books, status, quantities and time in force.
 * The BFF parses the cached id to `orderId` and the OLE date id to `tifDate`.
 * Instrument fields are present when that cache row has them.
 */
export const OmsOrder = Type.Object(
  {
    orderId: Type.Integer({ minimum: 1 }),
    books: Type.Array(Type.String({ minLength: 1 }), {
      description: 'Execution account and allocation account names. GET /oms/orders filters `book` against this list.',
    }),
    status: Type.String({ description: 'On-prem OrderStatus text, unchanged. Display labels come from the shared status map.' }),
    qty: Type.Number({ description: 'Order quantity in lots.' }),
    filledQty: Type.Number(),
    tifDate: nullableDate,
    orderNumber: Type.Optional(Type.String()),
    externalOrderNumber: Type.Optional(Type.String()),
    instrumentId: Type.Optional(Type.Integer()),
    bbTicker: Type.Optional(Type.String()),
    instrumentDescription: Type.Optional(Type.String()),
    exchange: Type.Optional(Type.String()),
    product: Type.Optional(Type.String()),
    contractMonthCode: Type.Optional(Type.String()),
    contractYear: Type.Optional(Type.String()),
    isSpread: Type.Optional(Type.Boolean()),
    side: Type.Optional(OmsSide),
    orderType: Type.Optional(Type.String()),
    limitPrice: Type.Optional(Type.Number()),
    stopPrice: Type.Optional(Type.Number()),
    tif: Type.Optional(Type.String()),
    startTime: Type.Optional(Type.String()),
    endTime: Type.Optional(Type.String()),
    algo: Type.Optional(Type.String({ description: 'Strategy name only. Never the numeric id.' })),
    algoParams: Type.Optional(OmsAlgoParams),
    broker: Type.Optional(Type.String()),
    executionAccount: Type.Optional(Type.String()),
    trader: Type.Optional(Type.String()),
    check: Type.Optional(OmsCheck),
    checkHits: Type.Optional(Type.Array(OmsCheckHit)),
    averageFillPrice: Type.Optional(Type.Number()),
    lastFillTime: Type.Optional(IsoDateTime),
    placedOn: Type.Optional(IsoDateTime),
    lastModifiedOn: Type.Optional(IsoDateTime),
    comments: Type.Optional(Type.String()),
    allocations: Type.Optional(Type.Array(OmsAllocation)),
    fills: Type.Optional(Type.Array(OmsFill)),
  },
  { $id: 'OmsOrder' },
);
export type OmsOrder = Static<typeof OmsOrder>;
export const omsOrderSchema = createSchema(OmsOrder);

export const OmsOrderList = Type.Object({ orders: Type.Array(OmsOrder) }, { $id: 'OmsOrderList' });
export type OmsOrderList = Static<typeof OmsOrderList>;
export const omsOrderListSchema = createSchema(OmsOrderList);

/** Draft sent to Order Preview. The required-before-send checklist is a later story. */
export const OmsOrderDraft = Type.Object(
  {
    instrumentId: Type.Integer(),
    side: OmsSide,
    qty: Type.Number(),
    orderType: Type.Optional(Type.String()),
    limitPrice: Type.Optional(Type.Number()),
    stopPrice: Type.Optional(Type.Number()),
    tif: Type.Optional(Type.String()),
    tifDate: Type.Optional(IsoDate),
    startTime: Type.Optional(Type.String()),
    endTime: Type.Optional(Type.String()),
    algo: Type.Optional(Type.String()),
    algoParams: Type.Optional(OmsAlgoParams),
    broker: Type.Optional(Type.String()),
    executionAccount: Type.Optional(Type.String()),
    comments: Type.Optional(Type.String()),
    allocations: Type.Optional(Type.Array(OmsAllocation)),
  },
  { $id: 'OmsOrderDraft' },
);
export type OmsOrderDraft = Static<typeof OmsOrderDraft>;
export const omsOrderDraftSchema = createSchema(OmsOrderDraft);

export const OmsOrderModify = Type.Object(
  {
    qty: Type.Optional(Type.Number()),
    limitPrice: Type.Optional(Type.Number()),
    stopPrice: Type.Optional(Type.Number()),
  },
  { $id: 'OmsOrderModify' },
);
export type OmsOrderModify = Static<typeof OmsOrderModify>;
export const omsOrderModifySchema = createSchema(OmsOrderModify);

export const OmsAllocationList = Type.Object(
  {
    allocations: Type.Array(OmsAllocation),
    fills: Type.Optional(Type.Array(OmsFill)),
  },
  { $id: 'OmsAllocationList' },
);
export type OmsAllocationList = Static<typeof OmsAllocationList>;
export const omsAllocationListSchema = createSchema(OmsAllocationList);

/** On-prem `getOrderAudit` text, unchanged. Cloud audit entries are a later story. */
export const OmsOrderHistory = Type.Object(
  { text: Type.String({ description: 'The desktop history string, including its line breaks.' }) },
  { $id: 'OmsOrderHistory' },
);
export type OmsOrderHistory = Static<typeof OmsOrderHistory>;
export const omsOrderHistorySchema = createSchema(OmsOrderHistory);

export const OmsBookRight = Type.Object({
  book: Type.String(),
  view: Type.Boolean(),
  send: Type.Boolean(),
  confirm: Type.Boolean(),
  waive: Type.Boolean(),
});
export type OmsBookRight = Static<typeof OmsBookRight>;

export const OmsKillSwitch = Type.Object(
  {
    on: Type.Boolean(),
    books: Type.Array(Type.String()),
    reason: Type.Optional(Type.String()),
  },
  { $id: 'OmsKillSwitch' },
);
export type OmsKillSwitch = Static<typeof OmsKillSwitch>;
export const omsKillSwitchSchema = createSchema(OmsKillSwitch);

export const OmsSession = Type.Object(
  {
    omsEnabled: Type.Boolean(),
    readEnabled: Type.Boolean(),
    writeEnabled: Type.Boolean({ description: 'Write guard for this session.' }),
    environmentBadge: Type.Optional(Type.String()),
    rights: Type.Array(OmsBookRight),
    permissionsAdmin: Type.Boolean(),
    killSwitch: OmsKillSwitch,
  },
  { $id: 'OmsSession' },
);
export type OmsSession = Static<typeof OmsSession>;
export const omsSessionSchema = createSchema(OmsSession);

export const OmsReference = Type.Object(
  {
    statuses: Type.Array(OmsDictionaryEntry, { description: 'GetOrderStatuses pairs, key and value, with no renaming.' }),
    orderTypes: Type.Array(OmsDictionaryEntry),
    tifTimes: Type.Array(OmsDictionaryEntry),
    partitions: Type.Array(OmsDictionaryEntry),
    traders: Type.Array(Type.String()),
    brokers: Type.Array(Type.String()),
    accounts: Type.Array(Type.String()),
  },
  { $id: 'OmsReference' },
);
export type OmsReference = Static<typeof OmsReference>;
export const omsReferenceSchema = createSchema(OmsReference);

export const OMS_BLOTTER_COLUMN_IDS = [
  'exchange',
  'product',
  'year',
  'month',
  'symbol',
  'status',
  'position',
  'workingBuys',
  'workingSells',
  'projected',
  'side',
  'target',
  'filled',
  'working',
  'inactive',
  'orderId',
  'first',
  'avg',
  'last',
] as const;
export type OmsBlotterColumnId = (typeof OMS_BLOTTER_COLUMN_IDS)[number];

export const OMS_POSITION_COLUMN_IDS = [
  'symbol',
  'bid',
  'last',
  'ask',
  'long',
  'short',
  'opening',
  'filled',
  'working',
  'closing',
  'closePlusOrders',
  'daily',
  'mtd',
  'qtd',
  'ytd',
] as const;
export type OmsPositionColumnId = (typeof OMS_POSITION_COLUMN_IDS)[number];

export const OMS_FILL_COLUMN_IDS = [
  'tradeTime',
  'execId',
  'parent',
  'exchange',
  'symbol',
  'side',
  'qty',
  'price',
  'currency',
  'marketValue',
  'strategy',
  'clearingAccount',
  'importerStatus',
] as const;
export type OmsFillColumnId = (typeof OMS_FILL_COLUMN_IDS)[number];

export const OMS_LAYOUT_COLUMN_MIN_WIDTH = 40;
export const OMS_LAYOUT_COLUMN_MAX_WIDTH = 600;

export const OmsLayoutColumn = Type.Object({
  id: Type.String({ minLength: 1, maxLength: 32 }),
  width: Type.Optional(Type.Number({ minimum: OMS_LAYOUT_COLUMN_MIN_WIDTH, maximum: OMS_LAYOUT_COLUMN_MAX_WIDTH })),
  visible: Type.Boolean(),
});
export type OmsLayoutColumn = Static<typeof OmsLayoutColumn>;

export const OmsLayoutSort = Type.Object({
  id: Type.String({ minLength: 1, maxLength: 32 }),
  direction: StringEnum(['asc', 'desc']),
});
export type OmsLayoutSort = Static<typeof OmsLayoutSort>;

export const OmsFillGroupBy = StringEnum(['none', 'order', 'product']);
export type OmsFillGroupBy = Static<typeof OmsFillGroupBy>;

export const OMS_SHARED_VIEW_ID_PATTERN = '^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$';

export const OmsLayout = Type.Object(
  {
    tab: OmsLayoutTab,
    layoutName: Type.String({ minLength: 1, maxLength: 64 }),
    columns: Type.Array(OmsLayoutColumn, { maxItems: OMS_BLOTTER_COLUMN_IDS.length }),
    /** Order and widths for the positions grid. The blotter columns stay in `columns`. */
    positionColumns: Type.Optional(Type.Array(OmsLayoutColumn, { maxItems: OMS_POSITION_COLUMN_IDS.length })),
    sort: Type.Optional(OmsLayoutSort),
    /** Where the user left the positions grouping. Tab `monitor` only. */
    positionGroupBy: Type.Optional(OmsPositionView),
    /** Where the user left the Trade Blotter grouping. Tab `blotter` only. */
    fillGroupBy: Type.Optional(OmsFillGroupBy),
    /** The saved Custom layout the user last opened or saved. Tab `custom` only, where `columns` is empty. */
    customLayoutId: Type.Optional(Type.String({ pattern: OMS_SHARED_VIEW_ID_PATTERN })),
    /** Optimistic concurrency — omit on first write. The store always returns a number. */
    version: Type.Optional(Type.Integer({ minimum: 0 })),
  },
  { $id: 'OmsLayout' },
);
export type OmsLayout = Static<typeof OmsLayout>;
export const omsLayoutSchema = createSchema(OmsLayout);

/** The one column layout kept per user and tab. The path still has a name; the trader does not pick one. */
export const OMS_COLUMN_LAYOUT_NAME = 'default';

export const OmsLayoutList = Type.Object({ layouts: Type.Array(OmsLayout) }, { $id: 'OmsLayoutList' });
export type OmsLayoutList = Static<typeof OmsLayoutList>;
export const omsLayoutListSchema = createSchema(OmsLayoutList);

/** Order blotter status strip choices: a group, or one status from a group menu. */
const OMS_ORDER_STATUS_GROUP_NAMES = ['Preview', 'Active', 'Rejected', 'Finished'] as const;
const OMS_ORDER_STATUS_LABEL_VALUES: string[] = Object.values(OMS_ORDER_STATUS_LABELS);
const OMS_FILTER_TEXT = Type.String({ minLength: 1, maxLength: 64 });

/** Order blotter filters a saved layout keeps: the status strip and the column searches. Never order rows. */
export const OmsOrderBlotterFilters = Type.Object({
  status: Type.Optional(
    Type.Object({
      name: StringEnum([...new Set<string>([...OMS_ORDER_STATUS_GROUP_NAMES, ...OMS_ORDER_STATUS_LABEL_VALUES])]),
      statuses: Type.Array(StringEnum(OMS_ORDER_STATUS_LABEL_VALUES), { minItems: 1, maxItems: OMS_ORDER_STATUS_LABEL_VALUES.length }),
    }),
  ),
  columnQueries: Type.Optional(
    Type.Array(Type.Object({ id: StringEnum([...OMS_BLOTTER_COLUMN_IDS]), query: OMS_FILTER_TEXT }), { maxItems: OMS_BLOTTER_COLUMN_IDS.length }),
  ),
});
export type OmsOrderBlotterFilters = Static<typeof OmsOrderBlotterFilters>;

export const OmsPositionFilters = Type.Object({ groupBy: OmsPositionView });
export type OmsPositionFilters = Static<typeof OmsPositionFilters>;

/** Trade Blotter filters. The trade date belongs to the shell and is not saved. */
export const OmsFillFilters = Type.Object({
  allocation: OmsFillAllocation,
  book: Type.Optional(OMS_FILTER_TEXT),
  exchange: Type.Optional(OMS_FILTER_TEXT),
  product: Type.Optional(OMS_FILTER_TEXT),
  side: Type.Optional(OmsSide),
  q: Type.Optional(OMS_FILTER_TEXT),
  groupBy: OmsFillGroupBy,
});
export type OmsFillFilters = Static<typeof OmsFillFilters>;

export const OMS_PANEL_IDS = ['orders', 'fills', 'positions'] as const;
export const OmsPanelId = StringEnum([...OMS_PANEL_IDS]);
export type OmsPanelId = Static<typeof OmsPanelId>;

export const OmsCustomLayoutPanel = Type.Object({
  id: OmsPanelId,
  width: Type.Integer({ minimum: 10, maximum: 100, description: 'Percent of its row.' }),
  columns: Type.Optional(
    Type.Array(OmsLayoutColumn, {
      maxItems: Math.max(OMS_BLOTTER_COLUMN_IDS.length, OMS_FILL_COLUMN_IDS.length, OMS_POSITION_COLUMN_IDS.length),
      description: "The panel grid's columns in order, with widths and visibility.",
    }),
  ),
  sort: Type.Optional(OmsLayoutSort),
});
export type OmsCustomLayoutPanel = Static<typeof OmsCustomLayoutPanel>;

/** Custom layout: panel ids, widths, and each panel's filters, columns and sort. Independent of each user's home-tab layouts. */
export const OmsCustomLayout = Type.Object({
  arrangement: StringEnum(['stacked', 'sideBySide']),
  panels: Type.Array(OmsCustomLayoutPanel, { minItems: 1, maxItems: OMS_PANEL_IDS.length }),
  filters: Type.Object({
    orders: Type.Optional(OmsOrderBlotterFilters),
    positions: Type.Optional(OmsPositionFilters),
    fills: Type.Optional(OmsFillFilters),
  }),
});
export type OmsCustomLayout = Static<typeof OmsCustomLayout>;

export const OMS_SHARED_VIEW_KINDS = ['custom'] as const;
export const OmsSharedViewKind = StringEnum([...OMS_SHARED_VIEW_KINDS]);
export type OmsSharedViewKind = Static<typeof OmsSharedViewKind>;
export const OmsSharedViewInput = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 64 }),
    custom: OmsCustomLayout,
  },
  { $id: 'OmsSharedViewInput', description: 'A name and the custom layout it saves.' },
);
export type OmsSharedViewInput = Static<typeof OmsSharedViewInput>;
export const omsSharedViewInputSchema = createSchema(OmsSharedViewInput);

/** A Custom layout, shared with everyone in the company (datasource). */
export const OmsSharedView = Type.Object(
  {
    id: Type.String({ pattern: OMS_SHARED_VIEW_ID_PATTERN }),
    kind: OmsSharedViewKind,
    ...OmsSharedViewInput.properties,
    createdBy: Type.String({ minLength: 1, description: 'The first writer. The BFF sends their name when it can resolve it.' }),
    updatedAt: IsoDateTime,
  },
  { $id: 'OmsSharedView' },
);
export type OmsSharedView = Static<typeof OmsSharedView>;
export const omsSharedViewSchema = createSchema(OmsSharedView);

export const OmsSharedViewList = Type.Object({ views: Type.Array(OmsSharedView) }, { $id: 'OmsSharedViewList' });
export type OmsSharedViewList = Static<typeof OmsSharedViewList>;
export const omsSharedViewListSchema = createSchema(OmsSharedViewList);

export const OmsSharedViewQuery = Type.Object({ kind: OmsSharedViewKind }, { $id: 'OmsSharedViewQuery' });
export type OmsSharedViewQuery = Static<typeof OmsSharedViewQuery>;
export const omsSharedViewQuerySchema = createSchema(OmsSharedViewQuery);

export const OmsInstrument = Type.Object({
  instrumentId: Type.Integer(),
  bbTicker: Type.String(),
  description: Type.String(),
  exchange: Type.Optional(Type.String()),
  product: Type.Optional(Type.String()),
  year: Type.Optional(Type.String()),
  type: Type.Optional(Type.String()),
  isSpread: Type.Optional(Type.Boolean()),
});
export type OmsInstrument = Static<typeof OmsInstrument>;

export const OmsInstrumentList = Type.Object({ instruments: Type.Array(OmsInstrument) }, { $id: 'OmsInstrumentList' });
export type OmsInstrumentList = Static<typeof OmsInstrumentList>;
export const omsInstrumentListSchema = createSchema(OmsInstrumentList);

export const OmsInstrumentPrice = Type.Object(
  {
    price: nullableNumber,
    asOf: nullableDate,
  },
  { $id: 'OmsInstrumentPrice', description: 'Latest close and its date.' },
);
export type OmsInstrumentPrice = Static<typeof OmsInstrumentPrice>;
export const omsInstrumentPriceSchema = createSchema(OmsInstrumentPrice);

export const OmsPosition = Type.Object({
  positionKey: Type.String({ minLength: 1 }),
  bbFullSymbol: Type.String(),
  view: OmsPositionView,
  groupLabel: Type.Optional(Type.String()),
  opening: Type.Union([Type.Number(), Type.Null()], {
    description: 'Signed. Null until the on-prem opening source is connected. Never zero for unknown.',
  }),
  filled: Type.Number({ description: 'Signed lots filled on the trade day. Sells negative.' }),
  working: Type.Number({ description: 'Signed lots on submitted orders not filled yet, pending included. Sells negative.' }),
  closing: Type.Union([Type.Number(), Type.Null()], { description: 'Opening + Filled. Null when opening is null.' }),
  closePlusOrders: Type.Union([Type.Number(), Type.Null()], { description: 'Opening + Filled + Working. Null when opening is null.' }),
  latestClose: Type.Optional(Type.Number()),
  latestCloseDate: Type.Optional(IsoDate),
});
export type OmsPosition = Static<typeof OmsPosition>;

export const OmsPositionList = Type.Object({ positions: Type.Array(OmsPosition) }, { $id: 'OmsPositionList' });
export type OmsPositionList = Static<typeof OmsPositionList>;
export const omsPositionListSchema = createSchema(OmsPositionList);

export const OmsOpenLot = Type.Object({
  lotId: Type.String({ minLength: 1 }),
  qty: Type.Number(),
  price: Type.Optional(Type.Number()),
  openedOn: Type.Optional(IsoDate),
});
export type OmsOpenLot = Static<typeof OmsOpenLot>;

export const OmsOpenLotList = Type.Object({ lots: Type.Array(OmsOpenLot) }, { $id: 'OmsOpenLotList' });
export type OmsOpenLotList = Static<typeof OmsOpenLotList>;
export const omsOpenLotListSchema = createSchema(OmsOpenLotList);

export const OmsRule = Type.Object(
  {
    ruleId: Type.String({ minLength: 1 }),
    name: Type.String(),
    stage: OmsRuleStage,
    enabled: Type.Boolean(),
    summary: Type.Optional(Type.String()),
    group: Type.String({ minLength: 1, description: 'Rule menu group, e.g. Eligibility, Size and price, Post trade.' }),
    available: Type.Boolean({
      description: 'True when HedgeFacts Applications or the Cloud runs this check. False when the check is coming soon.',
    }),
    description: Type.String(),
    appliedTo: Type.Union([Type.String(), Type.Null()], { description: 'Where on-prem applies the check. Null when on-prem holds no scope.' }),
    lastResult: Type.Union([OmsCheck, Type.Null()], {
      description:
        'Cloud rules: Pass, or the logic hit, from the live order read on the rules list. Null when no Preview ticket exists or that read failed. Desktop and coming-soon rules stay null.',
    }),
    effect: Type.Optional(OmsRuleEffect),
    source: OmsRuleSource,
    logic: Type.Union([OmsRuleLogic, Type.Null()]),
    hitOrderIds: Type.Optional(Type.Array(Type.Integer({ minimum: 1 }))),
  },
  { $id: 'OmsRule' },
);
export type OmsRule = Static<typeof OmsRule>;
export const omsRuleSchema = createSchema(OmsRule);

export const OmsRuleList = Type.Object({ rules: Type.Array(OmsRule) }, { $id: 'OmsRuleList' });
export type OmsRuleList = Static<typeof OmsRuleList>;
export const omsRuleListSchema = createSchema(OmsRuleList);

/** One stored setting for a rule. Rows with the same cap name are read together. A scope field left out matches every order. */
export const OmsRuleCapRow = Type.Object(
  {
    cap: Type.String({ minLength: 1 }),
    value: Type.Union([Type.Number(), Type.String(), Type.Boolean()]),
    book: Type.Optional(Type.String()),
    product: Type.Optional(Type.String()),
    symbol: Type.Optional(Type.String()),
    month: Type.Optional(Type.String()),
    year: Type.Optional(Type.String()),
    spotOnly: Type.Optional(Type.Boolean()),
  },
  { $id: 'OmsRuleCapRow' },
);
export type OmsRuleCapRow = Static<typeof OmsRuleCapRow>;
export const omsRuleCapRowSchema = createSchema(OmsRuleCapRow);

export const OmsRuleCapList = Type.Object({ rows: Type.Array(OmsRuleCapRow) }, { $id: 'OmsRuleCapList' });
export type OmsRuleCapList = Static<typeof OmsRuleCapList>;
export const omsRuleCapListSchema = createSchema(OmsRuleCapList);

export const OmsRuleUpdate = Type.Object(
  {
    enabled: Type.Optional(Type.Boolean()),
    summary: Type.Optional(Type.String()),
    /** Full set of caps for this rule. Sent as an array, it replaces every cap with those names, including a cap another rule shares. An empty array clears them. */
    caps: Type.Optional(Type.Array(OmsRuleCapRow)),
  },
  { $id: 'OmsRuleUpdate' },
);
export type OmsRuleUpdate = Static<typeof OmsRuleUpdate>;
export const omsRuleUpdateSchema = createSchema(OmsRuleUpdate);

export const OmsRuleSaved = Type.Object({ rule: OmsRule, caps: Type.Array(OmsRuleCapRow) }, { $id: 'OmsRuleSaved' });
export type OmsRuleSaved = Static<typeof OmsRuleSaved>;
export const omsRuleSavedSchema = createSchema(OmsRuleSaved);

export const OmsLimitRow = Type.Object({
  positionKey: Type.String({ minLength: 1, description: 'Matches OmsPosition.positionKey.' }),
  softLimit: nullableNumber,
  hardLimit: nullableNumber,
  spreadNet: Type.Union([Type.Number(), Type.Null()], {
    description: 'Net of spread legs as on-prem computed it. Never summed in the Cloud.',
  }),
});
export type OmsLimitRow = Static<typeof OmsLimitRow>;

export const OmsPostTradeLimits = Type.Object({ rows: Type.Array(OmsLimitRow) }, { $id: 'OmsPostTradeLimits' });
export type OmsPostTradeLimits = Static<typeof OmsPostTradeLimits>;
export const omsPostTradeLimitsSchema = createSchema(OmsPostTradeLimits);

export const OmsFillList = Type.Object(
  {
    fills: Type.Array(OmsFill),
    nextCursor: Type.Optional(Type.String()),
    total: Type.Integer({ minimum: 0, description: 'Fills that match the filter, every page.' }),
    boughtQty: Type.Number({ description: 'Buy fill quantity. Not added to sells.' }),
    soldQty: Type.Number({ description: 'Sell fill quantity. Not added to buys.' }),
    unsidedQty: Type.Number({ description: 'Quantity on fills whose order has no side.' }),
    marketValueTotal: Type.Optional(
      Type.Number({ description: 'Present only when every filtered fill has a market value in the same currency. Never calculated.' }),
    ),
    facets: Type.Object(
      {
        books: Type.Array(Type.String()),
        exchanges: Type.Array(Type.String()),
        products: Type.Array(Type.String()),
      },
      { description: 'Values on the trade day fills before any filter.' },
    ),
  },
  { $id: 'OmsFillList' },
);
export type OmsFillList = Static<typeof OmsFillList>;
export const omsFillListSchema = createSchema(OmsFillList);

export const OmsFillImportItem = Type.Object({
  orderId: Type.Integer({ minimum: 1 }),
  importerStatus: Type.String(),
});
export type OmsFillImportItem = Static<typeof OmsFillImportItem>;

export const OmsFillImportStatus = Type.Object({ items: Type.Array(OmsFillImportItem) }, { $id: 'OmsFillImportStatus' });
export type OmsFillImportStatus = Static<typeof OmsFillImportStatus>;
export const omsFillImportStatusSchema = createSchema(OmsFillImportStatus);

export const OmsApprovedUser = Type.Object({
  userId: Type.String({ minLength: 1 }),
  email: Type.String({ format: 'email' }),
  name: Type.String(),
  books: Type.Array(Type.String()),
  approved: Type.Boolean(),
});
export type OmsApprovedUser = Static<typeof OmsApprovedUser>;

export const OmsApprovedUserList = Type.Object({ users: Type.Array(OmsApprovedUser) }, { $id: 'OmsApprovedUserList' });
export type OmsApprovedUserList = Static<typeof OmsApprovedUserList>;
export const omsApprovedUserListSchema = createSchema(OmsApprovedUserList);

export const OmsApprovedUserRequestInput = Type.Object(
  {
    email: Type.String({ format: 'email' }),
    books: Type.Array(Type.String({ minLength: 1 }), { minItems: 1 }),
    reason: Type.Optional(Type.String()),
  },
  { $id: 'OmsApprovedUserRequestInput' },
);
export type OmsApprovedUserRequestInput = Static<typeof OmsApprovedUserRequestInput>;
export const omsApprovedUserRequestInputSchema = createSchema(OmsApprovedUserRequestInput);

export const OmsApprovedUserRequest = Type.Object(
  {
    requestId: Type.String({ minLength: 1 }),
    email: Type.String({ format: 'email' }),
    books: Type.Array(Type.String()),
    status: OmsApprovalStatus,
    reason: Type.Optional(Type.String()),
  },
  { $id: 'OmsApprovedUserRequest' },
);
export type OmsApprovedUserRequest = Static<typeof OmsApprovedUserRequest>;
export const omsApprovedUserRequestSchema = createSchema(OmsApprovedUserRequest);

export const OmsReason = Type.Object({ reason: Type.String({ minLength: 1 }) }, { $id: 'OmsReason' });
export type OmsReason = Static<typeof OmsReason>;
export const omsReasonSchema = createSchema(OmsReason);

export const OmsConfirmRequest = Type.Object(
  {
    orderIds: Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 }),
    acknowledgements: Type.Array(
      Type.Object({
        orderId: Type.Integer({ minimum: 1 }),
        checkNames: Type.Array(Type.String()),
        reason: Type.Optional(Type.String()),
      }),
    ),
  },
  { $id: 'OmsConfirmRequest' },
);
export type OmsConfirmRequest = Static<typeof OmsConfirmRequest>;
export const omsConfirmRequestSchema = createSchema(OmsConfirmRequest);

export const OmsRejectRequest = Type.Object(
  {
    orderIds: Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 }),
    reason: Type.String({ minLength: 1 }),
  },
  { $id: 'OmsRejectRequest' },
);
export type OmsRejectRequest = Static<typeof OmsRejectRequest>;
export const omsRejectRequestSchema = createSchema(OmsRejectRequest);

export const OmsTransitionResult = Type.Object(
  {
    results: Type.Array(
      Type.Object({
        orderId: Type.Integer({ minimum: 1 }),
        outcome: OmsTransitionOutcome,
        reason: Type.Optional(Type.String()),
      }),
    ),
  },
  { $id: 'OmsTransitionResult' },
);
export type OmsTransitionResult = Static<typeof OmsTransitionResult>;
export const omsTransitionResultSchema = createSchema(OmsTransitionResult);

export const OmsAuditEntry = Type.Object({
  id: Type.String({ minLength: 1 }),
  at: IsoDateTime,
  actor: Type.String(),
  entity: Type.String(),
  entityId: Type.String(),
  action: Type.String(),
  before: Type.Optional(Type.String()),
  after: Type.Optional(Type.String()),
  reason: Type.Optional(Type.String()),
});
export type OmsAuditEntry = Static<typeof OmsAuditEntry>;

export const OmsAuditList = Type.Object({ entries: Type.Array(OmsAuditEntry) }, { $id: 'OmsAuditList' });
export type OmsAuditList = Static<typeof OmsAuditList>;
export const omsAuditListSchema = createSchema(OmsAuditList);

export const OmsAuditCsv = Type.String({
  $id: 'OmsAuditCsv',
  description: 'CSV export of the audit list',
  contentMediaType: 'text/csv',
});
export type OmsAuditCsv = Static<typeof OmsAuditCsv>;
export const omsAuditCsvSchema = createSchema(OmsAuditCsv);

export const OmsApplyLayout = Type.Object({ layoutName: Type.String({ minLength: 1 }) }, { $id: 'OmsApplyLayout' });
export type OmsApplyLayout = Static<typeof OmsApplyLayout>;
export const omsApplyLayoutSchema = createSchema(OmsApplyLayout);

export const OmsBreach = Type.Object({
  breachId: Type.String({ minLength: 1 }),
  ruleId: Type.String(),
  book: Type.Optional(Type.String()),
  summary: Type.String(),
  openedOn: IsoDateTime,
});
export type OmsBreach = Static<typeof OmsBreach>;

export const OmsBreachList = Type.Object({ breaches: Type.Array(OmsBreach) }, { $id: 'OmsBreachList' });
export type OmsBreachList = Static<typeof OmsBreachList>;
export const omsBreachListSchema = createSchema(OmsBreachList);

export const OmsLayoutQuery = Type.Object({ tab: Type.Optional(OmsLayoutTab) }, { $id: 'OmsLayoutQuery' });
export type OmsLayoutQuery = Static<typeof OmsLayoutQuery>;
export const omsLayoutQuerySchema = createSchema(OmsLayoutQuery);

/** Calendar dates. The BFF converts them to the order-read Lambda dateIDFrom and dateIDTo. */
export const OmsOrderQuery = Type.Object(
  {
    from: Type.Optional(IsoDate),
    to: Type.Optional(IsoDate),
    book: Type.Optional(Type.String()),
    exchange: Type.Optional(Type.String()),
    product: Type.Optional(Type.String()),
    status: Type.Optional(Type.String()),
    side: Type.Optional(OmsSide),
    q: Type.Optional(Type.String()),
  },
  {
    $id: 'OmsOrderQuery',
    description: 'Omit from and to for the live window (today and tomorrow). A history range ends before today.',
  },
);
export type OmsOrderQuery = Static<typeof OmsOrderQuery>;
export const omsOrderQuerySchema = createSchema(OmsOrderQuery);

export const OmsPreviewQuery = Type.Object(
  {
    book: Type.Optional(Type.String()),
    exchange: Type.Optional(Type.String()),
    product: Type.Optional(Type.String()),
    check: Type.Optional(OmsCheck),
    side: Type.Optional(OmsSide),
    q: Type.Optional(Type.String()),
  },
  { $id: 'OmsPreviewQuery', description: 'Accepted and not applied. Order Preview filters the tickets on screen.' },
);
export type OmsPreviewQuery = Static<typeof OmsPreviewQuery>;
export const omsPreviewQuerySchema = createSchema(OmsPreviewQuery);

export const OmsInstrumentQuery = Type.Object(
  {
    type: Type.Optional(Type.String()),
    exchange: Type.Optional(Type.String()),
    product: Type.Optional(Type.String()),
    year: Type.Optional(Type.String({ pattern: '^\\d{4}$' })),
  },
  { $id: 'OmsInstrumentQuery' },
);
export type OmsInstrumentQuery = Static<typeof OmsInstrumentQuery>;
export const omsInstrumentQuerySchema = createSchema(OmsInstrumentQuery);

export const OmsPositionQuery = Type.Object(
  {
    view: Type.Optional(OmsPositionView),
    book: Type.Optional(Type.String()),
  },
  { $id: 'OmsPositionQuery' },
);
export type OmsPositionQuery = Static<typeof OmsPositionQuery>;
export const omsPositionQuerySchema = createSchema(OmsPositionQuery);

export const OmsRuleQuery = Type.Object({ stage: Type.Optional(OmsRuleStage) }, { $id: 'OmsRuleQuery' });
export type OmsRuleQuery = Static<typeof OmsRuleQuery>;
export const omsRuleQuerySchema = createSchema(OmsRuleQuery);

export const OmsFillQuery = Type.Object(
  {
    cursor: Type.Optional(Type.String()),
    from: Type.Optional(IsoDate),
    to: Type.Optional(IsoDate),
    book: Type.Optional(Type.String()),
    exchange: Type.Optional(Type.String()),
    product: Type.Optional(Type.String()),
    side: Type.Optional(OmsSide),
    q: Type.Optional(Type.String()),
    allocation: Type.Optional(OmsFillAllocation),
    pageSize: Type.Optional(OmsFillPageSize),
    group: Type.Optional(OmsFillGroup),
  },
  {
    $id: 'OmsFillQuery',
    description:
      'Omit allocation for every fill. Allocated means the fill has a clearing account. Book, exchange, product, side and q are applied before paging. pageSize defaults to 50. group pages whole orders or products, so one group is never split. from and to are accepted and not applied.',
  },
);
export type OmsFillQuery = Static<typeof OmsFillQuery>;
export const omsFillQuerySchema = createSchema(OmsFillQuery);

export const OmsAuditQuery = Type.Object(
  {
    entity: Type.Optional(Type.String()),
    entityId: Type.Optional(Type.String()),
    from: Type.Optional(IsoDate),
    to: Type.Optional(IsoDate),
  },
  { $id: 'OmsAuditQuery' },
);
export type OmsAuditQuery = Static<typeof OmsAuditQuery>;
export const omsAuditQuerySchema = createSchema(OmsAuditQuery);

export const OmsIdempotencyHeaders = Type.Object(
  {
    'Idempotency-Key': Type.String({ minLength: 1, description: 'Client request id. Repeating it must not create a second ticket.' }),
  },
  { $id: 'OmsIdempotencyHeaders' },
);
export type OmsIdempotencyHeaders = Static<typeof OmsIdempotencyHeaders>;
export const omsIdempotencyHeadersSchema = createSchema(OmsIdempotencyHeaders);

export const OmsPreviewTicketPutHeaders = Type.Object(
  {
    'Idempotency-Key': Type.String({ minLength: 1, description: 'Client request id. Repeating it must not create a second ticket.' }),
    'If-Match': Type.String({ minLength: 1, description: 'Order lastModifiedOn. A stale value is a conflict.' }),
  },
  { $id: 'OmsPreviewTicketPutHeaders' },
);
export type OmsPreviewTicketPutHeaders = Static<typeof OmsPreviewTicketPutHeaders>;
export const omsPreviewTicketPutHeadersSchema = createSchema(OmsPreviewTicketPutHeaders);
